import React, { useState, useEffect } from "react";
import { Typography, Card, Space, Spin, Row, Col } from "antd";
import {
  DatabaseOutlined,
  FileTextOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { usePermissions } from "../../../../PermissionsProvider";
import { DatasetService } from "../../../../services/DatasetService";
import { FinetuneDataset } from "../../../../model_gen/finetuning";

const { Title, Text } = Typography;

interface ReviewStepProps {
  selectedDataset: string;
  exportPrompt: string;
  fourbit: boolean;
  baseModel: string;
  displayName: string;
  description?: string;
  promptDeletions: string[];
  warmupSteps: number;
  learningRate: number;
  trainSplit: number;
  selectedSubDataset?: FinetuneDataset | null;
}

interface Dataset {
  id: number;
  name: string;
  description?: string;
  intent_group_name?: string;
  application_name?: string;
  intent_name?: string;
  request_count?: number;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedDataset,
  exportPrompt,
  fourbit,
  baseModel,
  displayName,
  description,
  promptDeletions,
  warmupSteps,
  learningRate,
  trainSplit,
  selectedSubDataset,
}) => {
  const { apiKey } = usePermissions();
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    if (selectedDataset) {
      DatasetService.getDataset(
        parseInt(selectedDataset),
        setIsLoading,
        apiKey
      ).subscribe(
        (data: any) => {
          if (data) {
            setDataset(data);
          }
        },
        (error: Error) => {
          console.error("Error fetching dataset details:", error);
        }
      );
    }
  }, [selectedDataset, apiKey]);

  // Helper function to get source name
  const getSourceName = (dataset: Dataset | FinetuneDataset): string => {
    if (dataset.intent_group_name) {
      return dataset.intent_group_name;
    } else if (dataset.application_name && dataset.intent_name) {
      return `${dataset.application_name} / ${dataset.intent_name}`;
    }
    return "Custom";
  };

  // Reusable function to render dataset details
  const renderDatasetDetails = (
    dataset: Dataset | FinetuneDataset | null,
    isLoading: boolean,
    errorMessage: string
  ) => {
    if (isLoading) {
      return (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <Spin />
          <p>Loading dataset details...</p>
        </div>
      );
    }

    if (!dataset) {
      return <Text type="warning">{errorMessage}</Text>;
    }

    return (
      <Row gutter={[24, 16]}>
        <Col xs={24} sm={12}>
          <Text type="secondary">Name</Text>
          <div>{dataset.name}</div>
        </Col>

        <Col xs={24} sm={12}>
          <Text type="secondary">Description</Text>
          <div>{dataset.description || "No description"}</div>
        </Col>

        <Col xs={24} sm={12}>
          <Text type="secondary">Source</Text>
          <div>{getSourceName(dataset)}</div>
        </Col>

        <Col xs={24} sm={12}>
          <Text type="secondary">Requests</Text>
          <div>
            {dataset.request_count !== undefined
              ? dataset.request_count
              : "N/A"}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Title level={4}>Review Finetune Configuration</Title>

      <Card
        title={
          <Space>
            <DatabaseOutlined />
            <span>Dataset</span>
          </Space>
        }
        style={{ marginBottom: 16 }}>
        {renderDatasetDetails(dataset, isLoading, "No dataset selected")}
      </Card>

      {selectedSubDataset && (
        <Card
          title={
            <Space>
              <DatabaseOutlined />
              <span>Sub Dataset</span>
            </Space>
          }
          style={{ marginBottom: 16 }}>
          {renderDatasetDetails(
            selectedSubDataset,
            false,
            "Failed to load sub dataset details"
          )}
        </Card>
      )}

      <Card
        title={
          <Space>
            <AppstoreOutlined />
            <span>Finetune Settings</span>
          </Space>
        }
        style={{ marginBottom: 16 }}>
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={8}>
            <Text type="secondary">Display Name</Text>
            <div>{displayName || "Not specified"}</div>
          </Col>

          <Col xs={24} sm={16}>
            <Text type="secondary">Description</Text>
            <div>{description || "No description"}</div>
          </Col>

          <Col xs={24} sm={8}>
            <Text type="secondary">Base Model</Text>
            <div>{baseModel}</div>
          </Col>

          <Col xs={24} sm={8}>
            <Text type="secondary">4-bit Quantization</Text>
            <div>{fourbit ? "Yes" : "No"}</div>
          </Col>

          <Col xs={24} sm={8}>
            <Text type="secondary">Warmup Steps</Text>
            <div>{warmupSteps}</div>
          </Col>

          <Col xs={24} sm={8}>
            <Text type="secondary">Learning Rate</Text>
            <div>{learningRate}</div>
          </Col>

          <Col xs={24} sm={8}>
            <Text type="secondary">Train Split</Text>
            <div>{trainSplit * 100}%</div>
          </Col>
        </Row>
      </Card>

      {exportPrompt && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>Prompt</span>
            </Space>
          }>
          <div style={{ whiteSpace: "pre-wrap" }}>{exportPrompt}</div>
        </Card>
      )}

      {promptDeletions && promptDeletions.length > 0 && (
        <Card
          title={
            <Space>
              <FileTextOutlined />
              <span>Prompt Deletions</span>
            </Space>
          }>
          <Space direction="vertical" style={{ width: "100%" }}>
            {promptDeletions.map((deletion, index) => (
              <div key={index}>{deletion}</div>
            ))}
          </Space>
        </Card>
      )}
    </Space>
  );
};

export default ReviewStep;
